import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import React from "react";
import Typography from "@mui/material/Typography";
import { motion } from "motion/react";
import { useOrchestrationStatus } from "../hooks/Starters/useOrchestrationStatus";
import { useParams } from "react-router";
import TerminalLog from "./TerminalLog";
import Stack from "@mui/material/Stack";

interface CommitProgressProps {
    shouldFadeOut?: boolean;
    onFadeComplete?: () => void;
}

export default function CommitProgress({
    shouldFadeOut,
    onFadeComplete,
}: CommitProgressProps = {}) {
    const { instanceId } = useParams<{ instanceId: string }>();

    // Use instanceId from URL parameters
    const { data: status, isLoading } = useOrchestrationStatus(
        instanceId ?? "",
    );

    const phase = status?.currentPhase ?? "Starting...";
    const percent = status?.percentComplete ?? 0;
    const done =
        status?.runtimeStatus === "Completed" ||
        status?.runtimeStatus === "Failed" ||
        status?.runtimeStatus === "Terminated";

    // Debug logging
    console.log(
        "CommitProgress render - shouldFadeOut:",
        shouldFadeOut,
        "done:",
        done,
    );

    return (
        <motion.div
            animate={shouldFadeOut ? "hidden" : "visible"}
            variants={{
                visible: { opacity: 1 },
                hidden: { opacity: 0 },
            }}
            transition={{ duration: 0.3 }}
            onAnimationComplete={(definition) => {
                console.log(
                    "Animation completed:",
                    definition,
                    "shouldFadeOut:",
                    shouldFadeOut,
                );
                if (definition === "hidden" && onFadeComplete) {
                    console.log("Calling onFadeComplete");
                    onFadeComplete();
                }
            }}
        >
            <Box sx={{ p: 3 }}>
                {isLoading && <Typography>Preparing workflow...</Typography>}

                {!isLoading && (
                    <Stack spacing={4}>
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="body1" gutterBottom>
                                {phase}
                            </Typography>

                            <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{ height: 10, borderRadius: 1 }}
                            />

                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {percent}%
                            </Typography>
                        </Box>
                        <TerminalLog
                            currentPhase={status?.currentPhase}
                            isCompleted={done}
                            customCompletionMessage={
                                status?.runtimeStatus === "Completed"
                                    ? "✓ Workflow completed successfully"
                                    : status?.runtimeStatus === "Failed"
                                      ? "✖ Workflow failed"
                                      : "Process terminated"
                            }
                        />

                        {status?.runtimeStatus === "Failed" && status?.errorMessage && (
                            <Box
                                sx={{
                                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                                    border: "1px solid",
                                    borderColor: "error.main",
                                    borderRadius: 1,
                                    padding: 2,
                                }}
                            >
                                <Typography
                                    color="error"
                                    gutterBottom
                                    variant="subtitle2"
                                >
                                    Error
                                </Typography>
                                <Typography color="error" variant="body2">
                                    {status.errorMessage}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}
            </Box>
        </motion.div>
    );
}
